import React, { useEffect, useState, useContext, useCallback } from 'react'
import api from '../../services/api'
import AuthContext from '../../contexts/Auth'

interface Alerta {
  id: number
  titulo: string
  mensagem: string
  tipo: 'info' | 'warning' | 'danger' | 'success'
  link_url?: string
  link_texto?: string
}

const TIPO_COLORS: Record<string, { bg: string; border: string; text: string; btn: string }> = {
  info:    { bg: '#e8f4fd', border: '#3498db', text: '#1a5276', btn: '#3498db' },
  warning: { bg: '#fef9e7', border: '#f39c12', text: '#7d6608', btn: '#f39c12' },
  danger:  { bg: '#fdedec', border: '#e74c3c', text: '#922b21', btn: '#e74c3c' },
  success: { bg: '#eafaf1', border: '#27ae60', text: '#1e8449', btn: '#27ae60' },
}

const TIPO_ICONS: Record<string, string> = {
  info:    'ni ni-notification-70',
  warning: 'ni ni-bell-55',
  danger:  'ni ni-support-16',
  success: 'ni ni-check-bold',
}

const ROTACAO_MS = 30000

const AlertaBanner: React.FC = () => {
  const { user } = useContext(AuthContext)
  const [alertas, setAlertas] = useState<Alerta[]>([])
  const [index, setIndex] = useState(0)
  const [visible, setVisible] = useState(true)

  const loadAlertas = useCallback(async () => {
    try {
      const { data } = await api.get('common/alertas/ativos')
      setAlertas(data.rows ?? data)
      setIndex(0)
      setVisible(true)
    } catch {
      // silencioso
    }
  }, [])

  useEffect(() => {
    if (user) loadAlertas()
  }, [user])

  useEffect(() => {
    if (alertas.length <= 1) return
    const id = setInterval(() => {
      setIndex(i => (i + 1) % alertas.length)
    }, ROTACAO_MS)
    return () => clearInterval(id)
  }, [alertas.length])

  const dispensar = async (id: number) => {
    try {
      await api.post(`common/alertas/${id}/dispensar`)
      const restantes = alertas.filter(a => a.id !== id)
      setAlertas(restantes)
      setIndex(i => Math.min(i, restantes.length - 1))
    } catch {
      // silencioso
    }
  }

  if (!visible || alertas.length === 0) return null

  const alerta = alertas[index]
  const cores = TIPO_COLORS[alerta.tipo] || TIPO_COLORS.info
  const total = alertas.length

  return (
    <div
      style={{
        background: cores.bg,
        borderLeft: `4px solid ${cores.border}`,
        color: cores.text,
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 14,
        position: 'relative',
        flexWrap: 'wrap',
        minHeight: 44,
      }}
    >
      <i className={TIPO_ICONS[alerta.tipo] || TIPO_ICONS.info} style={{ fontSize: 18, flexShrink: 0 }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        {alerta.titulo && (
          <strong style={{ marginRight: 6 }}>{alerta.titulo}:</strong>
        )}
        <span>{alerta.mensagem}</span>
        {alerta.link_url && (
          <a
            href={alerta.link_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: 6, color: cores.border, fontWeight: 600, textDecoration: 'underline' }}
          >
            {alerta.link_texto || 'Clique aqui'}
          </a>
        )}
      </div>

      {total > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <button
            onClick={() => setIndex(i => (i - 1 + total) % total)}
            style={navBtnStyle(cores.btn)}
            title="Anterior"
          >
            ‹
          </button>
          <span style={{ fontSize: 12, fontWeight: 600 }}>
            {index + 1}/{total}
          </span>
          <button
            onClick={() => setIndex(i => (i + 1) % total)}
            style={navBtnStyle(cores.btn)}
            title="Próximo"
          >
            ›
          </button>
        </div>
      )}

      <button
        onClick={() => dispensar(alerta.id)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: cores.text,
          fontSize: 18,
          lineHeight: 1,
          padding: '0 4px',
          flexShrink: 0,
          opacity: 0.7,
        }}
        title="Fechar"
      >
        ×
      </button>
    </div>
  )
}

function navBtnStyle(color: string) {
  return {
    background: 'none',
    border: `1px solid ${color}`,
    borderRadius: 4,
    color,
    cursor: 'pointer',
    fontSize: 18,
    lineHeight: 1,
    padding: '0 6px',
    fontWeight: 700,
  } as React.CSSProperties
}

export default AlertaBanner
